/* eslint-disable react/jsx-key */
/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import * as React from 'react';

import {
    ISignUpEmailVerification,
    ISignUpGivenNameVerification,
    ISignUpItem,
    ISignUpLoading,
    ISignUpSurnameVerification,
    ISignUpViewProps
} from './new-sign-up';
import SignUpButtonComponent from './components/new-sign-up-button';
import PhoneInputComponent from '../../common/components/phone-number-input';

import { format } from '@msdyn365-commerce-modules/utilities';
import { trackVClubRegistration } from '../../themes/venchi/utilities/datalayer';

const SignUpItem: React.FC<ISignUpItem> = ({ wrapper, label, errorMessage, input }) => {
    return (
        <Node {...wrapper}>
            {label}
            {errorMessage}
            {input}
        </Node>
    );
};

const SignUpEmailVerification: React.FC<ISignUpEmailVerification> = ({ email, buttonWrapper, buttons, successMessage, errorMessage }) => {
    return (
        <>
            {successMessage &&
                successMessage.map((message: React.ReactNode) => {
                    return <>{message}</>;
                })}
            <SignUpItem {...email} />
            {errorMessage &&
                errorMessage.map((message: React.ReactNode) => {
                    return <>{message}</>;
                })}
            <Node {...buttonWrapper}>
                {buttons &&
                    buttons.map((button: React.ReactNode) => {
                        return <>{button}</>;
                    })}
            </Node>
        </>
    );
};

/**
 * Handles validation of sign-up givenName.
 * @param event - Content editable event.
 * @param event.givenName - Event Given Name.
 * @param event.errorMessage - Event Error Message.
 * @returns - Error Message for Given Name.
 */
const SignUpGivenNameVerificationComponent: React.FC<ISignUpGivenNameVerification> = ({ givenName, errorMessage }) => {
    return (
        <>
            <SignUpItem {...givenName} />
            {errorMessage.map((message: React.ReactNode) => {
                return <>{message}</>;
            })}
        </>
    );
};

/**
 * Handles validation of signUp surname.
 * @param event - Content editable event.
 * @param event.surname - Event surname.
 * @param event.errorMessage - Event Error Message.
 * @returns - Error Message for surname.
 */
const SignUpSurnameVerificationComponent: React.FC<ISignUpSurnameVerification> = ({ surname, errorMessage }) => {
    return (
        <>
            <SignUpItem {...surname} />
            {errorMessage.map((message: React.ReactNode) => {
                return <>{message}</>;
            })}
        </>
    );
};

const LocalAccount: React.FC<ISignUpViewProps> = props => {
    const {
        localAccount,
        items,
        emailVerification,
        givenNameVerification,
        surnameVerification,
        buttons,
        errorMessage,
        disclaimer
    } = props.signUpLocalAccount;

    const locale = props.locale;
    const regionCRM = props.regionCRM;
    const EcomSignUpLegalEntity = props.EcomSignUpLegalEntity;
    let LastConsentSource: string;

    switch (props.LastConsentSource) {
        case 'FinanceOperationEU':
            LastConsentSource = 'Finance&Operation EU';
            break;
        case 'FinanceOperationUS':
            LastConsentSource = 'Finance&Operation US';
            break;
        default:
            LastConsentSource = 'Finance&Operation EU';
    }

    const [birthDate, setBirthDate] = React.useState('');
    const [formatDateState, setFormatDateState] = React.useState(true);
    const pattern = /^((1[6789]|[2-9][0-9])[0-9]{2}\/(0[13578]|1[02])\/(0[1-9]|[12][0-9]|3[01]))$|^((1[6789]|[2-9][0-9])[0-9]{2}\/(0[469]|11)\/(0[1-9]|[12][0-9]|30))$|^((16|[248][048]|[3579][26])00)|(1[6789]|[2-9][0-9])(0[48]|[13579][26]|[2468][048])\/02\/(0[1-9]|1[0-9]|2[0-9])$|^(1[6789]|[2-9][0-9])[0-9]{2}\/02\/(0[1-9]|1[0-9]|2[0-8])$/gm;
    const patternUS = /^((0[13578]|1[02])\/(0[1-9]|[12][0-9]|3[01]))$|^((0[469]|11)\/(0[1-9]|[12][0-9]|30))$|^02\/(0[1-9]|1[0-9]|2[0-9])$/gm;

    // This error is shown under the register button.
    const [phoneErrorMessage, setPhoneErrorMessage] = React.useState('');

    const checkDate = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        const today = new Date();
        if (value !== '' && (!value.match(locale !== 'en-US' ? pattern : patternUS) || today < new Date(value))) {
            setFormatDateState(false);
        } else {
            setFormatDateState(true);
        }
        if (locale === 'en-US') {
            setBirthDate(`1900/${value}`);
        }
    };

    if (typeof window !== 'undefined') {
        const signUpButton: HTMLElement = document.getElementById('continue') as HTMLElement;
        if (signUpButton !== undefined) {
            signUpButton.onclick = e => {
                if (!formatDateState) {
                    e.preventDefault();
                    e.stopPropagation();
                }
                trackVClubRegistration();
            };
        }
    }

    const profiling_ref = React.useRef<HTMLInputElement>(null);
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (locale.toLowerCase() === 'en-us') {
            const { checked } = e.target;
            profiling_ref.current!.checked = checked;
        }
    };

    const currentChannelCountryRegionISOCode = props.channelCountryRegionISOCode;

    // DONE guest state session variable; check that user ID (the one that's NOT an FC) exists alongside the user not being auth (since if not auth = loyalty)

    return (
        <Node {...localAccount}>
            {items &&
                items.map((item: ISignUpItem) => {
                    switch (item.key) {
                        case 'email':
                            if (emailVerification.isRequired) {
                                return (
                                    <Node {...emailVerification.verificationControlWrapper}>
                                        <SignUpItem {...item} />
                                        <SignUpEmailVerification {...emailVerification} />
                                    </Node>
                                );
                            } else {
                                const emailRepErrorText: string = props.resources.emailRepErrorText ?? null;
                                return (
                                    <>
                                        <SignUpItem
                                            key={item.key}
                                            wrapper={item.wrapper}
                                            label={item.label}
                                            errorMessage={item.errorMessage}
                                            input={
                                                <input
                                                    type='email'
                                                    id={item.key}
                                                    name={item.key}
                                                    required
                                                    onChange={() => {
                                                        if (typeof window !== 'undefined') {
                                                            const email: HTMLInputElement = document.getElementById(
                                                                'email'
                                                            ) as HTMLInputElement;
                                                            const email_rep: HTMLInputElement = document.getElementById(
                                                                'email_rep'
                                                            ) as HTMLInputElement;
                                                            const email_rep_error = document.getElementById(
                                                                'text-error'
                                                            ) as HTMLParagraphElement;
                                                            const email_rep_error_backup = document.getElementById(
                                                                'text-error-backup'
                                                            ) as HTMLSpanElement;

                                                            email_rep.pattern = email.value;
                                                            // I hope the following works well with Azure
                                                            email_rep.title = emailRepErrorText;

                                                            if (email.value === email_rep.value) {
                                                                if (email_rep_error !== null) {
                                                                    email_rep_error.textContent = ' ';
                                                                } else {
                                                                    email_rep_error_backup.textContent = ' ';
                                                                }
                                                            } else {
                                                                if (email_rep_error !== null) {
                                                                    email_rep_error.textContent = emailRepErrorText;
                                                                } else {
                                                                    email_rep_error_backup.textContent = emailRepErrorText;
                                                                }
                                                            }
                                                        }
                                                    }}
                                                />
                                            }
                                        />
                                        <div className='ms-sign-up__account-item ms-sign-up__account-item-email_rep entry-item attrEntry'>
                                            <label id='email_rep_label' htmlFor='email_rep' className='ms-sign-up__account-item-label'>
                                                {props.resources.emailRepeatLabelText
                                                    ? props.resources.emailRepeatLabelText
                                                    : 'Repeat your email address *'}
                                            </label>
                                            <input
                                                type='text'
                                                id='email_rep'
                                                name='email_rep'
                                                required
                                                maxLength={255}
                                                className='ms-sign-up__account-item-input ms-sign-up__account-item-email_rep'
                                                onChange={() => {
                                                    const email: HTMLInputElement = document.getElementById('email') as HTMLInputElement;
                                                    const email_rep: HTMLInputElement = document.getElementById(
                                                        'email_rep'
                                                    ) as HTMLInputElement;
                                                    const email_rep_error = document.getElementById('text-error') as HTMLParagraphElement;
                                                    const email_rep_error_backup = document.getElementById(
                                                        'text-error-backup'
                                                    ) as HTMLSpanElement;

                                                    email.pattern = email_rep.value;

                                                    if (email.value === email_rep.value) {
                                                        if (email_rep_error !== null) {
                                                            email_rep_error.textContent = ' ';
                                                        } else {
                                                            email_rep_error_backup.textContent = ' ';
                                                        }
                                                    } else {
                                                        if (email_rep_error !== null) {
                                                            email_rep_error.textContent = emailRepErrorText;
                                                        } else {
                                                            email_rep_error_backup.textContent = emailRepErrorText;
                                                        }
                                                    }
                                                }}
                                            />
                                            <div
                                                className='ms-sign-up__item-error error itemLevel'
                                                role='alert'
                                                aria-live='assertive'
                                                aria-hidden='true'
                                            >
                                                {/* <p id='text-error' className='ms-sign-up__item-error-text'></p> */}
                                                <p id='text-error' style={{ color: '#C13533' }}></p>
                                            </div>
                                            <span id='text-error-backup' style={{ color: '#C13533' }}></span>
                                        </div>
                                    </>
                                );
                            }
                        case 'newPassword':
                            const passwordRepErrorText: string = props.resources.passwordRepErrorText ?? null;
                            return (
                                <>
                                    <SignUpItem
                                        key={item.key}
                                        wrapper={item.wrapper}
                                        label={item.label}
                                        errorMessage={item.errorMessage}
                                        input={
                                            <input
                                                type='password'
                                                id={item.key}
                                                name={item.key}
                                                required
                                                onChange={() => {
                                                    if (typeof window !== 'undefined') {
                                                        const newPassword: HTMLInputElement = document.getElementById(
                                                            'newPassword'
                                                        ) as HTMLInputElement;
                                                        const reenterPassword: HTMLInputElement = document.getElementById(
                                                            'reenterPassword'
                                                        ) as HTMLInputElement;
                                                        const reenterPassword_error = document.getElementById(
                                                            'password-text-error'
                                                        ) as HTMLParagraphElement;
                                                        const reenterPassword_error_backup = document.getElementById(
                                                            'password-text-error-backup'
                                                        ) as HTMLSpanElement;

                                                        reenterPassword.pattern = newPassword.value;
                                                        // I hope the following works well with Azure
                                                        reenterPassword.title = passwordRepErrorText;

                                                        if (newPassword.value === reenterPassword.value) {
                                                            if (reenterPassword_error !== null) {
                                                                reenterPassword_error.textContent = ' ';
                                                            } else {
                                                                reenterPassword_error_backup.textContent = ' ';
                                                            }
                                                        } else {
                                                            if (reenterPassword_error !== null) {
                                                                reenterPassword_error.textContent = passwordRepErrorText;
                                                            } else {
                                                                reenterPassword_error_backup.textContent = passwordRepErrorText;
                                                            }
                                                        }
                                                    }
                                                }}
                                            />
                                        }
                                    />
                                    <div className='ms-sign-up__account-item ms-sign-up__account-item-reenterPassword entry-item attrEntry'>
                                        <label
                                            id='reenterPassword_label'
                                            htmlFor='reenterPassword'
                                            className='ms-sign-up__account-item-label'
                                        >
                                            {props.resources.confirmPasswordLabelText
                                                ? props.resources.confirmPasswordLabelText
                                                : 'Confirm password *'}
                                        </label>
                                        <input
                                            type='password'
                                            id='reenterPassword'
                                            name='reenterPassword'
                                            required
                                            maxLength={255}
                                            className='ms-sign-up__account-item-input ms-sign-up__account-item-reenterPassword'
                                            onChange={() => {
                                                const newPassword: HTMLInputElement = document.getElementById(
                                                    'newPassword'
                                                ) as HTMLInputElement;
                                                const reenterPassword: HTMLInputElement = document.getElementById(
                                                    'reenterPassword'
                                                ) as HTMLInputElement;
                                                const reenterPassword_error = document.getElementById(
                                                    'password-text-error'
                                                ) as HTMLParagraphElement;
                                                const reenterPassword_error_backup = document.getElementById(
                                                    'password-text-error-backup'
                                                ) as HTMLSpanElement;

                                                newPassword.pattern = reenterPassword.value;

                                                if (newPassword.value === reenterPassword.value) {
                                                    if (reenterPassword_error !== null) {
                                                        reenterPassword_error.textContent = ' ';
                                                    } else {
                                                        reenterPassword_error_backup.textContent = ' ';
                                                    }
                                                } else {
                                                    if (reenterPassword_error !== null) {
                                                        reenterPassword_error.textContent = passwordRepErrorText;
                                                    } else {
                                                        reenterPassword_error_backup.textContent = passwordRepErrorText;
                                                    }
                                                }
                                            }}
                                        />
                                        <div
                                            className='ms-sign-up__item-error error itemLevel'
                                            role='alert'
                                            aria-live='assertive'
                                            aria-hidden='true'
                                        >
                                            <p id='password-text-error' style={{ color: '#C13533' }}></p>
                                        </div>
                                        <span id='password-text-error-backup' style={{ color: '#C13533' }}></span>
                                    </div>
                                </>
                            );
                        case 'givenName':
                            if (givenNameVerification.isRequired) {
                                return (
                                    <Node {...givenNameVerification.verificationControlWrapper}>
                                        <SignUpItem {...item} />
                                        <SignUpGivenNameVerificationComponent {...givenNameVerification} />
                                    </Node>
                                );
                            } else {
                                return <SignUpItem {...item} />;
                            }
                        case 'surname':
                            if (surnameVerification.isRequired) {
                                return (
                                    <Node {...surnameVerification.verificationControlWrapper}>
                                        <SignUpItem {...item} />
                                        <SignUpSurnameVerificationComponent {...surnameVerification} />
                                    </Node>
                                );
                            } else {
                                return <SignUpItem {...item} />;
                            }
                        case 'extension_Birthday':
                            return (
                                <>
                                    <div className={'ms-sign-up__account-item ms-sign-up__account-item-birthday entry-item attrEntry'}>
                                        <label className={'ms-sign-up__account-item-label'} htmlFor='birthday'>
                                            {props.resources.birthdayLabelText ? props.resources.birthdayLabelText : null}
                                            {locale === 'en-US' && !props.resources.birthdayLabelText.endsWith('(mm/dd)')
                                                ? '  (mm/dd)'
                                                : ''}
                                        </label>
                                        {/* TODO Test US elements */}
                                        {locale === 'en-US' ? (
                                            <>
                                                <input
                                                    type='text'
                                                    id='extension_Birthday_F'
                                                    maxLength={5}
                                                    name='extension_Birthday_F'
                                                    className='ms-sign-up__account-item-input ms-sign-up__account-item-birthday'
                                                    onKeyUp={e => checkDate(e)}
                                                    onPaste={e => e.preventDefault()}
                                                />
                                                <input
                                                    type='text'
                                                    id='extension_Birthday'
                                                    maxLength={10}
                                                    name='extension_Birthday'
                                                    className='ms-sign-up__account-item-input ms-sign-up__account-item-birthday hidden'
                                                    value={birthDate}
                                                />
                                            </>
                                        ) : (
                                            <>
                                                {/* // TODO? Validate that it's not in the future, and possibly that it's after at least 18 years */}
                                                <input
                                                    type='date'
                                                    id='birthday'
                                                    name='birthday'
                                                    className='ms-sign-up__account-item-input ms-sign-up__account-item-birthday'
                                                    onPaste={e => e.preventDefault()}
                                                    onChange={() => {
                                                        if (typeof window !== 'undefined') {
                                                            const birthday_proxy: HTMLInputElement = document.getElementById(
                                                                'birthday'
                                                            ) as HTMLInputElement;
                                                            const birthday_hidden: HTMLInputElement = document.getElementById(
                                                                'extension_Birthday'
                                                            ) as HTMLInputElement;
                                                            birthday_proxy.onchange = event => {
                                                                birthday_hidden.value = birthday_proxy.value;
                                                            };
                                                        }
                                                    }}
                                                />
                                                <input
                                                    type='text'
                                                    id='extension_Birthday'
                                                    name='extension_Birthday'
                                                    className='ms-sign-up__account-item-input ms-sign-up__account-item-birthday'
                                                    onPaste={e => e.preventDefault()}
                                                    style={{ display: 'none' }}
                                                />
                                            </>
                                        )}
                                    </div>
                                    {props.config.BirthdayText ? (
                                        <Msdyn365.RichTextComponent className='extra-text__birthday' text={props.config.BirthdayText} />
                                    ) : null}
                                </>
                            );
                        case 'postalCode':
                            return (
                                <>
                                    <SignUpItem {...item} />
                                    {props.config.PostalText ? (
                                        <Msdyn365.RichTextComponent className='extra-text__postalcode' text={props.config.PostalText} />
                                    ) : null}
                                </>
                            );
                        case 'phone_number':
                            return (
                                <>
                                    <SignUpItem
                                        wrapper={item.wrapper}
                                        key={item.key}
                                        label={item.label}
                                        errorMessage={item.errorMessage}
                                        input={
                                            <PhoneInputComponent
                                                channelCountryRegionISOCode={currentChannelCountryRegionISOCode}
                                                isCountryCodeHidden={props.config.hideCountryCodeSelector || false}
                                                phoneInputId={item.key}
                                                resources={{
                                                    phoneValidationErrorMin: props.resources.phoneValidationErrorMin,
                                                    phoneValidationErrorMax: props.resources.phoneValidationErrorMax,
                                                    phoneValidationErrorNumeric: props.resources.phoneValidationErrorNumeric
                                                }}
                                                onPhoneNumberChange={(
                                                    phoneNumber: string,
                                                    fullPhoneNumber: string,
                                                    hasValidationError: boolean
                                                ) => {
                                                    if (!hasValidationError) {
                                                        setPhoneErrorMessage('');
                                                    } else {
                                                        setPhoneErrorMessage(props.resources.phoneValidationErrorMessage);
                                                    }
                                                    const phoneNumberHidden: HTMLInputElement = document.getElementById(
                                                        'extension_PhoneNumber'
                                                    ) as HTMLInputElement;

                                                    phoneNumberHidden.value = phoneNumber ? fullPhoneNumber : '';
                                                }}
                                            />
                                        }
                                    />
                                    <input
                                        type='text'
                                        id='extension_PhoneNumber'
                                        name='extension_PhoneNumber'
                                        className='ms-sign-up__account-item-input ms-sign-up__account-item-phone_number'
                                        style={{ display: 'none' }}
                                    />
                                    {props.config.PhoneText ? (
                                        <Msdyn365.RichTextComponent className='extra-text__phone' text={props.config.PhoneText} />
                                    ) : null}
                                </>
                            );
                        case 'extension_GenericMarketing':
                            return (
                                <>
                                    <div className={`inputBox privacy_field_ctn ${props.config.hideMarketing && 'hidden'}`}>
                                        <label>
                                            <input
                                                type='checkbox'
                                                id='extension_GenericMarketing'
                                                name='extension_GenericMarketing'
                                                value={'true'}
                                                defaultChecked={props.config.preflagMarketing}
                                                onChange={handleChange}
                                            />
                                            {props?.resources?.htmlGenericMarketing &&
                                            props.config.privacyPolicyURL &&
                                            locale.toLowerCase() !== 'en-us' ? (
                                                <p
                                                    className='privacy-disclaimer-M'
                                                    dangerouslySetInnerHTML={{
                                                        __html: format(
                                                            props?.resources?.htmlGenericMarketing,
                                                            props.config.privacyPolicyURL
                                                        )
                                                    }}
                                                />
                                            ) : (
                                                props.config.USmarketingText && (
                                                    <Msdyn365.RichTextComponent
                                                        className='privacy-disclaimer-M'
                                                        text={props?.config.USmarketingText}
                                                    />
                                                )
                                            )}
                                        </label>
                                    </div>
                                </>
                            );
                        case 'extension_Profiling':
                            return (
                                <>
                                    <div className={`inputBox privacy_field_ctn ${props.config.hideProfiling && 'hidden'}`}>
                                        <label>
                                            <input
                                                type='checkbox'
                                                id='extension_Profiling'
                                                name='extension_Profiling'
                                                value={'true'}
                                                defaultChecked={props.config.preflagProfiling}
                                                ref={profiling_ref}
                                            />
                                            {props?.resources?.htmlProfiling && props.config.privacyPolicyURL ? (
                                                <p
                                                    className='privacy-disclaimer-P'
                                                    dangerouslySetInnerHTML={{
                                                        __html: format(props?.resources?.htmlProfiling, props.config.privacyPolicyURL)
                                                    }}
                                                />
                                            ) : null}
                                        </label>
                                    </div>
                                </>
                            );
                        case 'extension_ThirdParty':
                            return (
                                <>
                                    <div className={`inputBox privacy_field_ctn ${props.config.hideThirdParty && 'hidden'}`}>
                                        <label>
                                            <input
                                                type='checkbox'
                                                id='extension_ThirdParty'
                                                name='extension_ThirdParty'
                                                value={'true'}
                                                defaultChecked={props.config.preflagThirdParty}
                                            />
                                            {props?.resources?.htmlThirdParty && props.config.privacyPolicyURL ? (
                                                <p
                                                    className='privacy-disclaimer-T'
                                                    dangerouslySetInnerHTML={{
                                                        __html: format(props?.resources?.htmlThirdParty, props.config.privacyPolicyURL)
                                                    }}
                                                />
                                            ) : null}
                                        </label>
                                    </div>
                                </>
                            );
                        case 'extension_RegionCRM':
                            return (
                                <div className={'region_crm_ctn'}>
                                    <input type='text' name='extension_RegionCRM' id='extension_RegionCRM' value={regionCRM} />
                                </div>
                            );
                        case 'extension_LastConsentSource':
                            return (
                                <div className={'lastConsentSource_ctn'}>
                                    <input
                                        type='text'
                                        name='extension_LastConsentSource'
                                        id='extension_LastConsentSource'
                                        value={LastConsentSource}
                                    />
                                </div>
                            );
                        case 'extension_EcomSignUpLegalEntity':
                            return (
                                <div className={'ecomSignUpLegalEntity_ctn'}>
                                    <input
                                        type='text'
                                        name='extension_EcomSignUpLegalEntity'
                                        id='extension_EcomSignUpLegalEntity'
                                        value={EcomSignUpLegalEntity}
                                    />
                                </div>
                            );
                        default:
                            return <SignUpItem {...item} />;
                    }
                })}
            {disclaimer}
            {errorMessage &&
                errorMessage.map((error: React.ReactNode, index: number) => {
                    return <React.Fragment key={index}>{error}</React.Fragment>;
                })}
            {buttons &&
                buttons.map((button: React.ReactNode, index: number) => {
                    // @ts-ignore
                    if (button?.key === 'continue') {
                        const isDisabled = phoneErrorMessage !== null && phoneErrorMessage !== undefined && phoneErrorMessage !== '';

                        return (
                            <React.Fragment key={index}>
                                <SignUpButtonComponent
                                    key='continue'
                                    id='continue' // @ts-ignore
                                    className={button.props.className} // @ts-ignore
                                    ariaLabel={button.props.ariaLabel} // @ts-ignore
                                    text={button.props.text}
                                    disabled={isDisabled}
                                />
                            </React.Fragment>
                        );
                    } else {
                        return <React.Fragment key={index}>{button}</React.Fragment>;
                    }
                })}
            {phoneErrorMessage && (
                <div className='ms-sign-up__item-error error itemLevel' role='alert'>
                    <p style={{ color: '#C13533' }}>{phoneErrorMessage}</p>
                </div>
            )}
        </Node>
    );
};

const SignUpLoading: React.FC<ISignUpLoading> = ({ modal, modalBody, icon, message }) => {
    return (
        <Node {...modal}>
            <Node {...modalBody}>
                {icon}
                {message}
            </Node>
        </Node>
    );
};

const SignUpView: React.FC<ISignUpViewProps> = props => {
    const { signUp, viewState, loading, defaultAADConainer, aadConainer, heading } = props;

    return (
        <Module {...signUp}>
            {viewState.isShowLoading && <Node {...defaultAADConainer} />}
            {viewState.isShowLoading && <SignUpLoading {...loading} />}
            <Node {...aadConainer}>
                {heading}
                {LocalAccount(props)}
            </Node>
        </Module>
    );
};

export default SignUpView;
